import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, notification } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { authenicateCode, login as loginAdapter } from '@/adapters/login';
import { IUserLogin, IUserLoginRes } from '@/interfaces/login';
import type { FormInstance, GetProps } from 'antd';

type OTPProps = GetProps<typeof Input.OTP>;
type PageType = 'authen' | 'login';

// Custom hook for authentication logic
const useAuthentication = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLoginSuccess = useCallback(
    (data: IUserLoginRes) => {
      if (!data) {
        throw new Error('Invalid login response');
      }

      notification.success({
        message: 'เข้าสู่ระบบสำเร็จ',
        description: 'login successful',
      });

      login(data);
      navigate('/');
    },
    [login, navigate],
  );

  const handleLoginError = useCallback((error: Error) => {
    console.log('Login error:', error.message);
    notification.error({
      message: 'เข้าสู่ระบบไม่สำเร็จ',
      description: 'รหัสผู้ใช้งาน หรือ รหัสผ่าน ไม่ถูกต้อง',
    });
  }, []);

  const loginMutation = useMutation({
    mutationFn: async (data: IUserLogin) => {
      try {
        const result = await loginAdapter(data);
        return result;
      } catch (error) {
        console.error('Error during loginAdapter call:', error);
        throw error;
      }
    },
    mutationKey: ['login'],
    onSuccess: handleLoginSuccess,
    onError: handleLoginError,
  });

  const authenticateCodeMutation = useMutation({
    mutationFn: async (data: IUserLogin) => {
      try {
        const result = await authenicateCode(data);
        return result;
      } catch (error) {
        console.error('Error during authenticateCode call:', error);
        throw error;
      }
    },
    mutationKey: ['authenticateCode'],
    onError: handleLoginError,
  });

  return {
    loginMutation,
    authenticateCodeMutation,
  };
};

// Form component for authentication
const AuthenticationForm: React.FC<{
  form: FormInstance<IUserLogin>;
  page: PageType;
  code: string;
  onChange: OTPProps['onChange'];
  onSubmit: (data: IUserLogin) => void;
  isLoading: boolean;
}> = ({ form, page, onChange, onSubmit, isLoading }) => {
  const sharedProps = useMemo(() => ({ onChange }), [onChange]);

  return (
    <Form
      form={form}
      name="login"
      initialValues={{ remember: true }}
      onFinish={onSubmit}
      layout="vertical"
    >
      <Form.Item
        label="รหัสผู้ใช้งาน"
        name="userName"
        rules={[{ required: true, message: 'Please input your username!' }]}
      >
        <Input size="large" disabled={page === 'login'} />
      </Form.Item>

      <Form.Item
        label="รหัสผ่าน"
        name="password"
        rules={[{ required: true, message: 'Please input your password!' }]}
        hidden={page === 'login'}
      >
        <Input.Password size="large" />
      </Form.Item>

      <Form.Item
        label="Verification Code"
        name="code"
        hidden={page === 'authen'}
        rules={[
          {
            required: page === 'login',
            message: 'Please input your verification code!',
          },
        ]}
      >
        <div className="flex justify-center">
          <Input.OTP
            formatter={str => str.toUpperCase()}
            length={4}
            size="large"
            {...sharedProps}
          />
        </div>
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          className="w-full mt-5"
          size="large"
          loading={isLoading}
        >
          เข้าสู่ระบบ
        </Button>
      </Form.Item>
    </Form>
  );
};

const Login: React.FC = () => {
  const [form] = Form.useForm<IUserLogin>();
  const [page, setPage] = useState<PageType>('authen');
  const [code, setCode] = useState<string>('');

  const { loginMutation, authenticateCodeMutation } = useAuthentication();

  const handleAuthentication = useCallback(
    async (data: IUserLogin) => {
      if (page === 'authen') {
        const result = await authenticateCodeMutation.mutateAsync(data);
        if (result) {
          setPage('login');
        } else {
          await loginMutation.mutateAsync(data);
        }
      } else {
        data.code = code;
        await loginMutation.mutateAsync(data);
      }
    },
    [page, code, authenticateCodeMutation, loginMutation],
  );

  const handleCodeChange: OTPProps['onChange'] = useCallback((text: string) => {
    setCode(text);
  }, []);

  const isLoading = useMemo(
    () => loginMutation.isPending || authenticateCodeMutation.isPending,
    [loginMutation.isPending, authenticateCodeMutation.isPending],
  );

  return (
    <div className="flex items-center flex-col justify-center min-h-screen alibox-login font-idm">
      <div className="flex flex-col justify-center w-full max-w-md p-8 space-y-8 bg-white rounded shadow-md">
        <div className="flex justify-center">
          <h2 className="text-2xl font-bold text-center">
            <img
              src="/allied-logo.jpg"
              alt="Kemin"
              style={{ maxHeight: 150 }}
            />
          </h2>
        </div>

        <div>
          <AuthenticationForm
            form={form}
            page={page}
            code={code}
            onChange={handleCodeChange}
            onSubmit={handleAuthentication}
            isLoading={isLoading}
          />
          <div className="flex justify-center text-gray-500 text-sm">
            v1.1.3 | alibox | 2026
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
